import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { financeApi } from '../../services/financeApi';
import { ProviderPayout } from '../../types/payouts';
import { formatAed } from './Money';

const DUBAI_DATE = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dubai', year: 'numeric', month: '2-digit', day: '2-digit' });

/** Today as `YYYY-MM-DD` in Asia/Dubai (finance dates are Dubai calendar days). */
function dubaiToday(): string {
  return DUBAI_DATE.format(new Date()).slice(0, 10);
}

interface FieldErrors {
  amountReceived?: string;
  payoutFee?: string;
  receivedOn?: string;
  reference?: string;
}

/**
 * Spec 067 DD4-A — record a Tabby payout that reached the customer-funds bank. Posting it clears the
 * Tabby receivable (account 1025) by amount received + payout fee. The payout fee is prefilled from
 * the Runtime Config default; a reference can be recorded once (409 on a repeat).
 */
export default function TabbyPayoutsCard() {
  const auth = useAuth();
  const [payouts, setPayouts] = useState<ProviderPayout[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [amountReceived, setAmountReceived] = useState('');
  const [payoutFee, setPayoutFee] = useState('');
  const [receivedOn, setReceivedOn] = useState(dubaiToday);
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitNote, setSubmitNote] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (prefillFee: boolean) => {
    if (!auth.token) return;
    setLoadError(null);
    try {
      const res = await financeApi.listProviderPayouts(auth.token);
      setPayouts(res.payouts);
      if (prefillFee && res.defaultPayoutFee != null) setPayoutFee(res.defaultPayoutFee.toFixed(2));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load Tabby payouts');
    }
  }, [auth.token]);

  useEffect(() => { load(true); }, [load]);

  const validate = (): FieldErrors => {
    const errors: FieldErrors = {};
    const amount = Number(amountReceived);
    if (amountReceived.trim() === '' || !Number.isFinite(amount) || amount <= 0) {
      errors.amountReceived = 'Enter the amount received';
    }
    const fee = Number(payoutFee);
    if (payoutFee.trim() === '' || !Number.isFinite(fee) || fee < 0) {
      errors.payoutFee = 'Enter the payout fee (0 or more)';
    }
    if (!receivedOn) errors.receivedOn = 'Enter the date received';
    else if (receivedOn > dubaiToday()) errors.receivedOn = 'Date received cannot be in the future';
    if (reference.trim() === '') errors.reference = 'Enter the Tabby payout reference';
    else if (reference.trim().length > 100) errors.reference = 'Reference must be at most 100 characters';
    return errors;
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!auth.token) return;
    setSubmitError(null);
    setSubmitNote(null);
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setSaving(true);
    try {
      const trimmedNotes = notes.trim();
      const saved = await financeApi.recordProviderPayout({
        provider: 'TABBY',
        amountReceived: Number(amountReceived),
        payoutFee: Number(payoutFee),
        receivedOn,
        reference: reference.trim(),
        ...(trimmedNotes ? { notes: trimmedNotes } : {}),
      }, auth.token);
      setSubmitNote(`Tabby payout ${saved.reference} recorded.`);
      setAmountReceived('');
      setReference('');
      setNotes('');
      await load(false);
    } catch (err) {
      // 409 = the reference is already recorded; 400 = a bad field. The server message says which.
      setSubmitError(err instanceof Error ? err.message : 'Failed to record the Tabby payout');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-card" style={{ marginTop: '1rem' }} data-testid="tabby-payouts-card">
      <div className="admin-card-header">
        <h3 className="admin-card-title">Tabby payouts</h3>
      </div>

      <form onSubmit={submit} noValidate aria-label="Record Tabby payout">
        <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.5rem' }}>Record Tabby payout</div>
        {submitNote && <div className="admin-info-banner info" role="status">{submitNote}</div>}
        {submitError && <div className="admin-info-banner danger" role="alert" data-testid="tabby-payout-error">{submitError}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(12rem, 100%), 1fr))', gap: '0.75rem' }}>
          <div className="admin-form-group">
            <label className="admin-form-label" htmlFor="tabby-amount-received">Amount received (AED)</label>
            <input id="tabby-amount-received" type="number" min="0" step="0.01" className="admin-form-input"
              value={amountReceived} onChange={(e) => setAmountReceived(e.target.value)} aria-invalid={!!fieldErrors.amountReceived} />
            {fieldErrors.amountReceived && <span style={{ color: 'var(--admin-danger)', fontSize: '0.75rem' }}>{fieldErrors.amountReceived}</span>}
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label" htmlFor="tabby-payout-fee">Payout fee (AED)</label>
            <input id="tabby-payout-fee" type="number" min="0" step="0.01" className="admin-form-input"
              value={payoutFee} onChange={(e) => setPayoutFee(e.target.value)} aria-invalid={!!fieldErrors.payoutFee} />
            {fieldErrors.payoutFee && <span style={{ color: 'var(--admin-danger)', fontSize: '0.75rem' }}>{fieldErrors.payoutFee}</span>}
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label" htmlFor="tabby-received-on">Date received</label>
            <input id="tabby-received-on" type="date" className="admin-form-input" max={dubaiToday()}
              value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} aria-invalid={!!fieldErrors.receivedOn} />
            {fieldErrors.receivedOn && <span style={{ color: 'var(--admin-danger)', fontSize: '0.75rem' }}>{fieldErrors.receivedOn}</span>}
          </div>
          <div className="admin-form-group">
            <label className="admin-form-label" htmlFor="tabby-reference">Reference</label>
            <input id="tabby-reference" type="text" maxLength={100} className="admin-form-input"
              value={reference} onChange={(e) => setReference(e.target.value)} aria-invalid={!!fieldErrors.reference} />
            {fieldErrors.reference && <span style={{ color: 'var(--admin-danger)', fontSize: '0.75rem' }}>{fieldErrors.reference}</span>}
          </div>
        </div>
        <div className="admin-form-group">
          <label className="admin-form-label" htmlFor="tabby-notes">Notes (optional)</label>
          <input id="tabby-notes" type="text" className="admin-form-input" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
          {saving ? 'Recording…' : 'Record Tabby payout'}
        </button>
      </form>

      <div style={{ marginTop: '1.25rem' }}>
        <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.5rem' }}>Recent Tabby payouts</div>
        {loadError && <div className="admin-info-banner danger" role="alert">{loadError}</div>}
        {payouts && payouts.length === 0 && <div className="admin-empty-state"><p>No Tabby payouts recorded yet.</p></div>}
        {payouts && payouts.length > 0 && (
          <div className="admin-table-wrapper">
            <table className="admin-table" data-testid="tabby-payouts-table">
              <thead>
                <tr>
                  <th>Received</th>
                  <th>Reference</th>
                  <th style={{ textAlign: 'right' }}>Amount received</th>
                  <th style={{ textAlign: 'right' }}>Payout fee</th>
                  <th style={{ textAlign: 'right' }}>Cleared</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map((p) => (
                  <tr key={p.id}>
                    <td>{p.receivedOn}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{p.reference}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatAed(p.amountReceived)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatAed(p.payoutFee)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatAed(p.cleared)}</td>
                    <td style={{ color: p.notes ? undefined : 'var(--admin-text-muted)' }}>{p.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
